import { NextLink } from 'components/NextLink';
import { ColorItem, CommonItemProps, ProductCategory, ProductCondition, SimpleItem } from 'types';
import { PRODUCT_CONDITIONS_MAP } from 'consts';

import cx from 'classnames';
import styles from './ProductPage.module.scss';
import Image from 'next/legacy/image';
import { GearVariantProps } from '../../components/GearVariants/GearVariants';
import { GearDetailsOptionProps } from '../../components/GearOptions/GearOptions';
import { Dispatch, SetStateAction } from 'react';

export type PageGearDetailsProps = {
  brand: CommonItemProps;
  category: ProductCategory;
  colors: ColorItem[];
  condition: ProductCondition;
  sizes: SimpleItem[];
  sizeGuide: string;
  technicalSpecification: string;
  useVariants: boolean;
  options: GearDetailsOptionProps[];
  variants: GearVariantProps[];
  selectedOptions: string[];
  availableVariants: GearVariantProps[];
  setSelectedOptions: Dispatch<SetStateAction<string[]>>;
  setAvailableVariants: Dispatch<SetStateAction<GearVariantProps[]>>;
};

export const PageGearDetails = ({
  brand,
  category,
  colors,
  condition,
  sizes,
  sizeGuide,
  technicalSpecification,
  useVariants,
  options,
  variants,
  selectedOptions,
  availableVariants,
  setSelectedOptions,
  setAvailableVariants,
}: PageGearDetailsProps) => {
  return (
    <div className={styles.gearDetails}>
      <div className={styles.mainDetailBlock}>
        {useVariants ? (
          <>
            {options.map((option, index) => (
              <div className={styles.gearDetailsOptions} key={option.id}>
                <p className={styles.detailBlockHeader}>{option.name}: <b>{selectedOptions[index]}</b></p>
                <div className={styles.itemWrapper}>
                  {option.values?.map((value, id) => {
                    const isSelected = selectedOptions[index]===value
                    const disabled = !variants?.some(v => v.options.every((o, i) => (i===index)
                      ? o.value===value
                      : !selectedOptions[i] || o.value===selectedOptions[i]
                    ))

                    return (
                      <div
                        key={id}
                        onClick={() => {
                          if(disabled)
                            return
                          const selectedO = [...selectedOptions]
                          selectedO[index] = value
                          setSelectedOptions(selectedO)
                          setAvailableVariants(variants.filter(v => v.options.every((o, i) => !selectedO[i] || o.value===selectedO[i])))
                        }}
                      >
                        <p
                          className={cx(
                            styles.borderedItem,
                            {
                              [styles.selected]: isSelected,
                              [styles.disabled]: disabled,
                            }
                          )}
                        >{value}</p>
                      </div>
                    )
                  })}
                </div>
              </div>
            ))}
          </>
        ) : (
          <>
            {!!sizes.length && (
              <div className={styles.gearInfoSize}>
                <div className={styles.gearDetailsOptions}>
                  <p>Size:</p>
                  <div className={styles.itemWrapper}>
                    {sizes.map((size, i) => (
                      <p key={i} className={styles.borderedItem}>
                        {size.title}
                      </p>
                    ))}
                  </div>
                </div>
                {sizeGuide && (
                  <div className={styles.sizeGuide}>
                    <Image alt="Favorite" width={24} height={24} src="/icons/design/CircleHalfDark.svg" />
                    <a href={sizeGuide} download="size_guide">
                      Size Guide
                    </a>
                  </div>
                )}
              </div>
            )}
            {!!colors.length && (
              <div className={styles.gearDetailsOptions}>
                <p className={styles.detailBlockHeader}>Colors:</p>
                <div className={styles.itemWrapper}>
                  {colors.map((color, index) => (
                    <div key={index} className={styles.borderedItem}>
                      <div className={styles.colorSwitch} style={{ backgroundColor: color.value }} />
                      <p>{color.title}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
        {category && (
          <div className={styles.gearDetailsOptions}>
            <p className={styles.detailBlockHeader}>Category:</p>
            <NextLink href={`/gear?category=${category.slug}`}>
              <div className={styles.itemWrapper}>
                <p className={cx(styles.borderedItem)}>{category.title}</p>
              </div>
            </NextLink>
          </div>
        )}
        {brand && (
          <div className={styles.gearDetailsOptions}>
            <p className={styles.detailBlockHeader}>Brand:</p>
            <NextLink href={`/gear?brands=["${brand.slug}"]`}>
              <div className={styles.itemWrapper}>
                <p className={cx(styles.borderedItem)}>{brand.title}</p>
              </div>
            </NextLink>
          </div>
        )}
        {(availableVariants?.[0]?.condition || condition) && (
          <div className={styles.gearDetailsOptions}>
            <p className={styles.detailBlockHeader}>Condition:</p>
            <div className={styles.itemWrapper}>
              <p
                className={cx(styles.borderedItem)}>{PRODUCT_CONDITIONS_MAP[availableVariants?.[0]?.condition || condition]}</p>
            </div>
          </div>
        )}
      </div>
      {technicalSpecification && (
        <div className={styles.gearDetailsTechnical}>
          <p className={styles.detailBlockHeader}>Technical Specification:</p>
          <p>{technicalSpecification}</p>
        </div>
      )}
    </div>
  );
};
